import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useClassNames } from './FocusPlaceholder.styles';
import type { FocusPlaceholderProps } from './FocusPlaceholder.types';

const FocusPlaceholder: FC<FocusPlaceholderProps> = ({ focusRef }) => {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const activate = () => {
      setIsActive(true);
    };

    focusRef(activate);
  }, [focusRef]);

  useEffect(() => {
    if (isActive) {
      // istanbul ignore next
      placeholderRef.current?.focus();
    }
  }, [isActive]);

  const deactivateAfterFocusLoss = useCallback(() => {
    if (placeholderRef.current !== document.activeElement) {
      setIsActive(false);
    }
  }, []);

  const classNames = useClassNames();

  if (!isActive) {
    return null;
  }

  // nbsp aria-label and child space necessary for screenreader compat; do not remove
  // without verifying that it still works with JAWS, VoiceOver, etc!
  return (
    <div role="presentation" className={classNames.anchor} tabIndex={-1}>
      <div
        className={classNames.placeholder}
        tabIndex={-1}
        ref={placeholderRef}
        onBlur={deactivateAfterFocusLoss}
        aria-label="&nbsp;"
      >
        {' '}
      </div>
    </div>
  );
};

export default FocusPlaceholder;
